/**
 * Villa12BhkPricingModePanel — admin toggle for the 12 BHK Celebration Villa
 * pricing mode. Two modes:
 *   • Manual (default): keeps the flagship price as set by the admin.
 *   • Auto-sum of 3+4+5: 12 BHK price = sum of its three component villas
 *     (3BHK + 4BHK + 5BHK) and auto-updates when any component price changes.
 *
 * Used by both:
 *   • /admin/rate-management (web admin dashboard)   → uses /api/admin/12bhk-pricing-mode
 *   • AdminCalRatesPanel (mobile calendar PWA)       → uses /api/admin-cal/{token}/12bhk-pricing-mode
 *
 * Pass `cal` + `token` to switch to the PWA endpoints; omit for web admin.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { FiCheck, FiZap, FiEdit3, FiLoader } from 'react-icons/fi';
import api from '../config/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => `Rs.${Number(n || 0).toLocaleString('en-IN')}`;

const Villa12BhkPricingModePanel = ({ cal = false, token = null }) => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const endpoint = cal
    ? `/admin-cal/${token}/12bhk-pricing-mode`
    : `/admin/12bhk-pricing-mode`;

  const fetchState = useCallback(async () => {
    setErr('');
    try {
      if (cal) {
        const r = await fetch(`${BACKEND_URL}/api${endpoint}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setState(await r.json());
      } else {
        const r = await api.get(endpoint);
        setState(r.data);
      }
    } catch (e) {
      setErr('Could not load 12 BHK pricing mode');
    }
  }, [endpoint, cal]);

  useEffect(() => { fetchState(); }, [fetchState]);

  const toggle = async (nextAuto) => {
    if (busy || !state) return;
    if (state.auto_sum === nextAuto) return;
    setBusy(true);
    setErr('');
    try {
      if (cal) {
        const r = await fetch(`${BACKEND_URL}/api${endpoint}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ auto_sum: nextAuto }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
      } else {
        await api.put(endpoint, { auto_sum: nextAuto });
      }
      await fetchState();
    } catch (e) {
      setErr('Failed to update pricing mode. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!state) {
    return (
      <div data-testid="villa-12bhk-mode-panel" className="rounded-xl bg-white/5 border border-white/10 p-4 mb-5">
        <div className="text-white/40 text-xs flex items-center gap-2">
          <FiLoader className="w-3 h-3 animate-spin" /> Loading 12 BHK pricing mode…
        </div>
      </div>
    );
  }

  const auto = state.auto_sum;
  const preview = state.preview_auto_sum || {};
  const manual = state.manual || {};

  // Both the web admin page AND the mobile PWA panel use dark theme — the
  // `cal` flag now only switches the backend endpoint, not the look.
  const wrap = 'rounded-xl bg-gradient-to-r from-[#F57C20]/10 to-[#F57C20]/5 border border-[#F57C20]/25 p-4 mb-5';
  const title = 'text-white text-sm font-semibold flex items-center gap-2';
  const sub = 'text-white/40 text-[11px] mt-0.5';
  const btnActive = 'bg-[#F57C20] text-white border-[#F57C20] shadow-lg';
  const btnIdle = 'bg-white/5 text-white/60 border-white/10 hover:border-[#F57C20]/40 hover:text-white';
  const price = 'text-white font-semibold';
  const label = 'text-white/50';

  return (
    <div data-testid="villa-12bhk-mode-panel" className={wrap}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className={title}>
            <FiZap className="w-4 h-4 text-[#F57C20]" />
            12 BHK Celebration Villa — Pricing Mode
          </p>
          <p className={sub}>
            Pick how the 12 BHK (3+4+5 compound) is priced on the website &amp; booking engine.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
        {/* Manual */}
        <button
          type="button"
          data-testid="villa-12bhk-mode-manual-btn"
          disabled={busy}
          onClick={() => toggle(false)}
          className={`text-left px-3 py-2.5 rounded-lg border text-xs transition-all ${!auto ? btnActive : btnIdle}`}
        >
          <span className="flex items-center gap-1.5 font-semibold">
            {!auto ? <FiCheck className="w-3.5 h-3.5" /> : <FiEdit3 className="w-3.5 h-3.5" />}
            Manual Flagship Pricing
          </span>
          <span className={`block mt-1 ${!auto ? 'text-white/90' : label}`}>
            <span className={!auto ? 'text-white' : price}>Weekday {fmt(manual.base_price)}</span>
            <span className="mx-1 opacity-50">·</span>
            <span className={!auto ? 'text-white' : price}>Weekend {fmt(manual.weekend_price)}</span>
          </span>
        </button>

        {/* Auto-sum */}
        <button
          type="button"
          data-testid="villa-12bhk-mode-auto-btn"
          disabled={busy}
          onClick={() => toggle(true)}
          className={`text-left px-3 py-2.5 rounded-lg border text-xs transition-all ${auto ? btnActive : btnIdle}`}
        >
          <span className="flex items-center gap-1.5 font-semibold">
            {auto ? <FiCheck className="w-3.5 h-3.5" /> : <FiZap className="w-3.5 h-3.5" />}
            Auto-Sum of 3 + 4 + 5 BHK
          </span>
          <span className={`block mt-1 ${auto ? 'text-white/90' : label}`}>
            <span className={auto ? 'text-white' : price}>Weekday {fmt(preview.base_price)}</span>
            <span className="mx-1 opacity-50">·</span>
            <span className={auto ? 'text-white' : price}>Weekend {fmt(preview.weekend_price)}</span>
          </span>
        </button>
      </div>

      {err && (
        <p className="text-red-500 text-[11px] mt-2" data-testid="villa-12bhk-mode-err">{err}</p>
      )}
      {busy && (
        <p className="text-white/40 text-[11px] mt-2 flex items-center gap-1">
          <FiLoader className="w-3 h-3 animate-spin" /> Updating…
        </p>
      )}
    </div>
  );
};

export default Villa12BhkPricingModePanel;
